import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useToast } from '../context/ToastContext.jsx'
import { track } from '../lib/analytics.js'
import Seo from '../components/Seo.jsx'

// Width presets for the embed. The static attribution <a> (in the host page's
// DOM, OUTSIDE the iframe) is the SEO backlink — an iframe's own links pass no
// link equity, so the credit anchor must live in the snippet itself.
const SIZES = {
  standard: { label: 'Standard', max: 460 },
  compact: { label: 'Compact', max: 360 },
  full: { label: 'Full width', max: null },
}

function buildSnippet(max) {
  const width = max ? `width:100%;max-width:${max}px` : 'width:100%'
  return `<iframe src="https://calculatemypeptides.com/embed" title="Peptide Reconstitution Calculator" loading="lazy" id="cmp-embed" style="border:0;${width};height:900px"></iframe>
<script>window.addEventListener("message",function(e){if(e.data&&e.data.cmpEmbedHeight){var f=document.getElementById("cmp-embed");if(f){f.style.height=e.data.cmpEmbedHeight+"px"}}});</script>
<p style="font:13px/1.5 system-ui,sans-serif;margin:8px 0 0">Free <a href="https://calculatemypeptides.com/" target="_blank" rel="noopener">Peptide Reconstitution Calculator</a> by CalculateMyPeptides</p>`
}

export default function EmbedInfo() {
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  const [size, setSize] = useState('standard')

  const snippet = buildSnippet(SIZES[size].max)
  const previewMax = SIZES[size].max || 620

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      track('copy_embed', { size }) // GA4: measures embed-kit pickup (backlinks)
      toast('Embed code copied', 'green')
      setTimeout(() => setCopied(false), 2500)
    } catch {
      toast('Couldn’t copy — select the code and copy manually', 'red')
    }
  }

  return (
    <main className="page">
      <Seo
        title="Embed the Peptide Calculator (Free) | CalculateMyPeptides"
        description="Add our free peptide reconstitution & dosing calculator to your website with one line of code. Give your readers an interactive dosing tool — no signup, no cost."
      />
      <div className="container page-narrow">
        <span className="pill blue" style={{ marginBottom: 10 }}>Free to embed</span>
        <h1 className="section-title">Embed the peptide calculator on your site</h1>
        <p className="section-sub" style={{ maxWidth: '60ch' }}>
          Add the reconstitution &amp; dosing calculator to any website, blog, or forum post — free,
          no signup. Paste the snippet below where you want it to appear. It’s responsive and resizes
          itself to fit.
        </p>

        <ul className="embed-benefits">
          <li>✅ One line of code — works in WordPress, Ghost, Webflow, Squarespace, raw HTML</li>
          <li>✅ Auto-resizes to its content, mobile-friendly, matches your page width</li>
          <li>✅ Always up to date — improvements ship to your embed automatically</li>
          <li>✅ Free forever; the only ask is the small attribution link stays</li>
        </ul>

        <div className="embed-grid">
          <div>
            <h2 className="pep-h2">1. Pick a size</h2>
            <div className="seg" role="tablist" aria-label="Embed width" style={{ marginBottom: 16 }}>
              {Object.entries(SIZES).map(([key, s]) => (
                <button key={key} type="button" role="tab" aria-selected={size === key}
                  className={size === key ? 'active' : ''} onClick={() => setSize(key)}>
                  {s.label}
                </button>
              ))}
            </div>

            <h2 className="pep-h2">2. Copy the code</h2>
            <div className="embed-code">
              <button className="btn btn-primary btn-sm embed-copy" onClick={copy}>
                {copied ? '✓ Copied' : 'Copy code'}
              </button>
              <textarea className="embed-textarea" readOnly rows={7} value={snippet}
                onFocus={(e) => e.target.select()} />
            </div>

            <h2 className="pep-h2" style={{ marginTop: 22 }}>3. Paste it on your page</h2>
            <p className="soft">
              Drop it into any HTML block (WordPress “Custom HTML” block, a raw-HTML section, etc.).
              Keep the small “by CalculateMyPeptides” credit line — that link is how we keep the tool free.
            </p>
            <p className="soft">
              Prefer to link instead? Point readers at the full{' '}
              <Link to="/calculator">peptide calculator</Link> or a specific{' '}
              <Link to="/charts">dosing chart</Link>.
            </p>
          </div>

          <div>
            <h2 className="pep-h2">Live preview</h2>
            <iframe
              key={size}
              src="/embed"
              title="Peptide Reconstitution Calculator preview"
              loading="lazy"
              style={{ border: '1px solid var(--border)', borderRadius: 14, width: '100%', maxWidth: previewMax, height: 900 }}
            />
          </div>
        </div>

        <p className="muted small" style={{ marginTop: 26 }}>
          Educational tool — not medical advice. By embedding, you agree to keep the attribution link intact.
        </p>
      </div>
    </main>
  )
}
